import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Users2, Plus, Edit3, Trash2, KeyRound, Mail, Shield, Search, Copy, UserCheck, UserX,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const PERMISSION_OPTIONS = [
  { key: 'clients', label: 'Clients', desc: 'Add / edit client companies & export workflow.' },
  { key: 'project_create', label: 'Project — Create', desc: 'Add new projects.' },
  { key: 'project_edit', label: 'Project — Edit', desc: 'Edit project name, client, status, dates, description.' },
  { key: 'project_delete', label: 'Project — Delete', desc: 'Delete projects (destructive — grant carefully).' },
  { key: 'task_create', label: 'Task — Create', desc: 'Assign new tasks to team members.' },
  { key: 'task_edit', label: 'Task — Edit', desc: 'Edit any task (title, assignee, deadline, priority). Assignees can always update their own status.' },
  { key: 'task_delete', label: 'Task — Delete', desc: 'Delete tasks (destructive — grant carefully).' },
  { key: 'rate_cards', label: 'Rate Cards', desc: 'View and edit service pricing.' },
  { key: 'leads', label: 'Leads', desc: 'Create / edit / view sales leads.' },
  { key: 'team_performance', label: 'Team Performance', desc: 'See leaderboard, departments & top clients.' },
  { key: 'finance_reviewer', label: 'Finance — Reviewer', desc: 'Review / approve / reject team transactions, see full Finance ledger, view Subscriptions & Books.' },
  { key: 'finance_admin', label: 'Finance — Admin', desc: 'Everything reviewer can do + manage Subscriptions, edit USD rate, mark reimbursements paid, import/link Books data. Implies reviewer.' },
  { key: 'client_profile', label: 'Can open client profiles', desc: 'Open a client card to see the full internal profile — graphs, hours, team members and billing. Owner / Admin always have access.' },
  // ─── New fine-grained module.action toggles (keep legacy ones ON for compat) ───
  { key: 'clients_create', label: 'Clients — Create', desc: 'Add new clients.' },
  { key: 'clients_edit', label: 'Clients — Edit', desc: 'Edit any client\'s details.' },
  { key: 'clients_delete', label: 'Clients — Delete', desc: 'Delete clients (destructive — grant carefully).' },
  { key: 'messages_view', label: 'Messages — Open', desc: 'Open the Messages inbox.' },
  { key: 'messages_send', label: 'Messages — Send', desc: 'Send mail or chat messages to the team.' },
  { key: 'see_others_hours', label: 'Privacy — See others\' hours', desc: 'See other people\'s hours on Daily Reports and client profiles.' },
  { key: 'see_money', label: 'Privacy — See money amounts', desc: 'See ₹ amounts (outstanding, lead values, budgets) outside Finance.' },
  { key: 'download_own_report', label: 'Privacy — Download own report', desc: 'Allow this user to download the "My report" of their own work.' },
];

const DEFAULT_PERMS_BY_ROLE = {
  Owner: { clients: true, project_create: true, project_edit: true, project_delete: true, task_create: true, task_edit: true, task_delete: true, rate_cards: true, leads: true, team_performance: true, finance_reviewer: true, finance_admin: true },
  Admin: { clients: true, project_create: true, project_edit: true, project_delete: true, task_create: true, task_edit: true, task_delete: true, rate_cards: true, leads: true, team_performance: true, finance_reviewer: true, finance_admin: true },
  Operations: { clients: false, project_create: true, project_edit: true, project_delete: true, task_create: true, task_edit: true, task_delete: true, rate_cards: false, leads: false, team_performance: true, finance_reviewer: false, finance_admin: false },
  'Digital Marketer': { clients: true, project_create: true, project_edit: false, project_delete: false, task_create: true, task_edit: false, task_delete: false, rate_cards: false, leads: true, team_performance: false, finance_reviewer: false, finance_admin: false },
  Sales: { clients: false, project_create: true, project_edit: false, project_delete: false, task_create: true, task_edit: false, task_delete: false, rate_cards: false, leads: true, team_performance: false, finance_reviewer: false, finance_admin: false },
  'Designer/Editor': { clients: false, project_create: false, project_edit: false, project_delete: false, task_create: false, task_edit: false, task_delete: false, rate_cards: false, leads: false, team_performance: false, finance_reviewer: false, finance_admin: false },
  'Content Writer/Creator': { clients: false, project_create: false, project_edit: false, project_delete: false, task_create: false, task_edit: false, task_delete: false, rate_cards: false, leads: false, team_performance: false, finance_reviewer: false, finance_admin: false },
  Accounts: { clients: false, project_create: false, project_edit: false, project_delete: false, task_create: false, task_edit: false, task_delete: false, rate_cards: false, leads: false, team_performance: false, finance_reviewer: true, finance_admin: false },
  'LED Van Operator': { clients: false, project_create: false, project_edit: false, project_delete: false, task_create: false, task_edit: false, task_delete: false, rate_cards: false, leads: false, team_performance: false, finance_reviewer: false, finance_admin: false },
  Intern: { clients: false, project_create: false, project_edit: false, project_delete: false, task_create: false, task_edit: false, task_delete: false, rate_cards: false, leads: false, team_performance: false, finance_reviewer: false, finance_admin: false },
  Client: { clients: false, project_create: false, project_edit: false, project_delete: false, task_create: false, task_edit: false, task_delete: false, rate_cards: false, leads: false, team_performance: false, finance_reviewer: false, finance_admin: false },
};

const INTERNAL_ROLES = [
  'Owner', 'Admin', 'Digital Marketer', 'Designer/Editor', 'Content Writer/Creator',
  'Sales', 'Operations', 'Accounts', 'LED Van Operator', 'Intern',
];
const ALL_ROLES = [...INTERNAL_ROLES, 'Client'];

const ROLE_COLOR = {
  Owner: 'border-amber-500/40 text-amber-200',
  Admin: 'border-amber-400/50 text-amber-100',
  Client: 'border-sky-500/40 text-sky-300',
};
const defaultColor = 'border-amber-500/30 text-amber-200/80';

// Access tab helpers — hoisted above the main component so the dialog can
// reference them without hoisting trouble.
function diffCount(form, initial) {
  if (!form || !initial) return 0;
  let n = 0;
  const simpleKeys = ['role', 'team', 'is_head', 'daily_reports_scope'];
  for (const k of simpleKeys) {
    if (JSON.stringify(form[k] ?? null) !== JSON.stringify(initial[k] ?? null)) n += 1;
  }
  const p1 = form.permissions || {};
  const p0 = initial.permissions || {};
  const allKeys = new Set([...Object.keys(p1), ...Object.keys(p0)]);
  for (const k of allKeys) {
    if (!!p1[k] !== !!p0[k]) n += 1;
  }
  if (JSON.stringify(form.report_user_ids || []) !== JSON.stringify(initial.report_user_ids || [])) n += 1;
  return n;
}

function AccessTabContent({ form, setForm, users, editingId, roleDefaults, teams, isSuperAdmin }) {
  const SELECTABLE_ROLES = Object.keys(roleDefaults || {}).filter(
    (r) => r !== 'Client' && (isSuperAdmin || !['Owner', 'Admin'].includes(r)),
  );
  const roleDefaultsForCurrent = roleDefaults[form.role] || {};
  const isCustomised = (() => {
    const p = form.permissions || {};
    for (const k of Object.keys({ ...p, ...roleDefaultsForCurrent })) {
      if (!!p[k] !== !!roleDefaultsForCurrent[k]) return true;
    }
    return false;
  })();

  const resetToDefaults = () => {
    setForm((f) => ({ ...f, permissions: { ...(roleDefaults[f.role] || {}) } }));
  };

  const MATRIX = [
    { group: 'Daily Reports', rows: [
      { key: 'dailyreports_edit_others', label: "Edit other people's entries" },
      { key: 'dailyreports_delete_others', label: "Delete other people's entries" },
    ]},
    { group: 'Tasks', rows: [
      { key: 'tasks_view', label: 'View all tasks' },
      { key: 'tasks_create', label: 'Create tasks' },
      { key: 'tasks_edit', label: 'Edit tasks' },
      { key: 'tasks_delete', label: 'Delete tasks' },
    ]},
    { group: 'Projects', rows: [
      { key: 'projects_create', label: 'Create projects' },
      { key: 'projects_edit', label: 'Edit projects' },
      { key: 'projects_delete', label: 'Delete projects' },
    ]},
    { group: 'Clients', rows: [
      { key: 'clients_create', label: 'Add clients' },
      { key: 'clients_edit', label: 'Edit clients' },
      { key: 'clients_delete', label: 'Delete clients' },
    ]},
    { group: 'Leads', rows: [
      { key: 'leads_view', label: 'View leads' },
      { key: 'leads_create', label: 'Add leads' },
      { key: 'leads_edit', label: 'Edit leads' },
      { key: 'leads_delete', label: 'Delete leads' },
    ]},
    { group: 'Messages', rows: [
      { key: 'messages_view', label: 'Open the inbox' },
      { key: 'messages_send', label: 'Send messages' },
    ]},
    { group: 'Finance', rows: [
      { key: 'finance_reviewer', label: 'Finance (reviewer)' },
      { key: 'finance_admin', label: 'Finance (admin)' },
    ]},
    { group: 'Assets', rows: [
      { key: 'assets_enabled',  label: 'Enable Assets module (master switch)' },
      { key: 'assets_view',     label: 'View (open the Assets tab, see Godown stock, My assets)' },
      { key: 'assets_view_all', label: 'View all (every asset, every person — the "By person" view)' },
      { key: 'assets_list',     label: 'List / add assets (add new assets, bulk import)' },
      { key: 'assets_manage',   label: 'Manage (edit any asset, change status, manage Godowns)' },
    ]},
    { group: 'Settings', locked: true, rows: [
      { key: '_settings', label: 'Settings — Owner / Admin only (locked)' },
    ]},
  ];

  const toggle = (key) => {
    setForm((f) => ({ ...f, permissions: { ...(f.permissions || {}), [key]: !f.permissions?.[key] } }));
  };

  const setRole = (role) => {
    setForm((f) => ({ ...f, role, permissions: { ...(roleDefaults[role] || {}) } }));
  };

  const inScope = form.daily_reports_scope || 'own';

  return (
    <div className="space-y-4" data-testid="user-access-tab">
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div>
            <Label className="text-amber-200">Role</Label>
            <div className="text-[11px] text-slate-400">The role sets the defaults; the matrix below overrides them.</div>
          </div>
          {isCustomised && (
            <span className="text-[11px] bg-amber-500/15 text-amber-200 border border-amber-500/40 px-2 py-0.5 rounded-full font-semibold" data-testid="user-customised-chip">
              customised
            </span>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {SELECTABLE_ROLES.map((r) => (
            <button key={r} type="button" onClick={() => setRole(r)}
                    data-selectable data-selected={form.role === r ? 'true' : 'false'}
                    className="flex-shrink-0 min-w-[150px] text-left rounded-lg bg-[#0f2240] border border-amber-500/20 px-3 py-2 transition"
                    data-testid={`role-card-${r}`}>
              <div className="text-sm font-semibold text-white">{r}</div>
              <div className="text-[10px] text-slate-400">
                {Object.values(roleDefaults[r] || {}).filter(Boolean).length} on by default
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg bg-[#0f2240]/60 border border-amber-500/15 p-3">
        <div className="font-semibold text-white text-sm mb-2">Team &amp; visibility</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-slate-400">Team</Label>
            <Input value={form.team || ''}
                   onChange={(e) => setForm((f) => ({ ...f, team: e.target.value }))}
                   placeholder="e.g. Design, Marketing" list="user-teams-list"
                   className="bg-[#0a1628] border-amber-500/20 h-9 text-sm"
                   data-testid="user-team" />
            <datalist id="user-teams-list">
              {teams.map((t) => <option key={t} value={t} />)}
            </datalist>
          </div>
          <label className="flex items-center gap-2 bg-[#0a1628] border border-amber-500/20 rounded-md p-2 cursor-pointer self-end">
            <Checkbox checked={!!form.is_head}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, is_head: !!v }))}
                      className="border-amber-500/40 data-[state=checked]:bg-amber-500 data-[state=checked]:text-[#0a1628]"
                      data-testid="user-is-head" />
            <span className="text-sm text-slate-200">Team head</span>
          </label>
        </div>
        <div className="mt-3">
          <Label className="text-[11px] text-slate-400">Daily Reports visibility</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
            {[
              { k: 'own', label: 'Own' },
              { k: 'team', label: 'My team' },
              { k: 'selected', label: 'Selected people' },
              { k: 'everyone', label: 'Everyone' },
            ].map((opt) => (
              <button key={opt.k} type="button"
                      onClick={() => setForm((f) => ({ ...f, daily_reports_scope: opt.k }))}
                      data-selectable data-selected={inScope === opt.k ? 'true' : 'false'}
                      className="rounded-md bg-[#0a1628] border border-amber-500/20 px-3 py-2 text-xs text-slate-200"
                      data-testid={`scope-${opt.k}`}>
                {opt.label}
              </button>
            ))}
          </div>
          {inScope === 'selected' && (
            <div className="mt-2 rounded-md bg-[#0a1628] border border-amber-500/20 p-2 max-h-32 overflow-auto" data-testid="scope-selected-list">
              <div className="text-[11px] text-slate-400 mb-1">Pick teammates whose entries this user can see:</div>
              {(users || []).filter((u) => u.id !== editingId && u.role !== 'Client').map((u) => {
                const checked = (form.report_user_ids || []).includes(u.id);
                return (
                  <label key={u.id} className="flex items-center gap-2 py-0.5 cursor-pointer text-sm text-slate-200">
                    <Checkbox checked={checked}
                              onCheckedChange={(v) => setForm((f) => ({
                                ...f,
                                report_user_ids: v
                                  ? [...(f.report_user_ids || []), u.id]
                                  : (f.report_user_ids || []).filter((x) => x !== u.id),
                              }))}
                              className="border-amber-500/40 data-[state=checked]:bg-amber-500 data-[state=checked]:text-[#0a1628]" />
                    <span className="truncate">{u.name} <span className="text-[11px] text-slate-500">· {u.role}</span></span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          Viewing wider never lets this user edit other people's entries — only Daily Reports admins can.
        </div>
      </div>

      <div className="rounded-lg bg-[#0f2240]/60 border border-amber-500/15 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold text-white text-sm">What they can do</div>
          <button type="button" onClick={resetToDefaults}
                  className="text-[11px] text-amber-200 hover:underline"
                  data-testid="reset-defaults-btn">
            Reset to {form.role} defaults
          </button>
        </div>
        {MATRIX.map((grp) => (
          <div key={grp.group} className="mb-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{grp.group}</div>
            {grp.rows.map((row) => {
              if (grp.locked) {
                return (
                  <div key={row.key} className="flex items-center justify-between py-1.5 opacity-60">
                    <span className="text-sm text-slate-300">{row.label}</span>
                    <span className="text-[10px] bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">locked</span>
                  </div>
                );
              }
              const nowOn = !!form.permissions?.[row.key];
              const defOn = !!roleDefaultsForCurrent[row.key];
              const changed = nowOn !== defOn;
              return (
                <label key={row.key} htmlFor={`matrix-${row.key}`}
                       className="flex items-center justify-between py-1 pl-1 pr-1.5 rounded hover:bg-[#0a1628]/60 cursor-pointer"
                       data-testid={`matrix-row-${row.key}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Checkbox id={`matrix-${row.key}`} checked={nowOn}
                              onCheckedChange={() => toggle(row.key)}
                              className="border-amber-500/40 data-[state=checked]:bg-amber-500 data-[state=checked]:text-[#0a1628]"
                              data-testid={`matrix-check-${row.key}`} />
                    <span className="text-sm text-slate-200 truncate">{row.label}</span>
                  </div>
                  {changed && (
                    <span className="text-[10px] bg-amber-500/15 text-amber-200 border border-amber-500/40 px-1.5 py-0.5 rounded font-semibold">
                      changed
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        ))}
      </div>

      <div className="rounded-lg bg-[#0f2240]/60 border border-amber-500/15 p-3" data-testid="privacy-card">
        <div className="font-semibold text-white text-sm mb-2">Privacy</div>
        {[
          { key: 'see_others_hours', label: "See other people's hours",
            help: 'Default ON for Owner, Admin, Operations and team heads.' },
          { key: 'see_money', label: 'See money amounts',
            help: 'Default ON for Owner, Admin, Accounts and finance reviewers. Hides ₹ on Clients, Leads and Projects elsewhere.' },
          { key: 'download_own_report', label: 'Download reports (own work only)',
            help: 'Default ON for everyone — safe one-pager with no team hours.' },
          { key: 'client_profile', label: 'Open client profiles',
            help: 'Default ON for Owner and Admin only.' },
        ].map((p) => {
          const nowOn = !!form.permissions?.[p.key];
          const defOn = !!roleDefaultsForCurrent[p.key];
          const changed = nowOn !== defOn;
          return (
            <label key={p.key} htmlFor={`priv-${p.key}`}
                   className="flex items-start justify-between py-1.5 cursor-pointer"
                   data-testid={`privacy-row-${p.key}`}>
              <div className="flex items-start gap-2 min-w-0">
                <Checkbox id={`priv-${p.key}`} checked={nowOn}
                          onCheckedChange={() => toggle(p.key)}
                          className="border-amber-500/40 data-[state=checked]:bg-amber-500 data-[state=checked]:text-[#0a1628] mt-0.5"
                          data-testid={`privacy-check-${p.key}`} />
                <div className="min-w-0">
                  <div className="text-sm text-slate-200">{p.label}</div>
                  <div className="text-[11px] text-slate-500">{p.help}</div>
                </div>
              </div>
              {changed && (
                <span className="text-[10px] bg-amber-500/15 text-amber-200 border border-amber-500/40 px-1.5 py-0.5 rounded font-semibold ml-2 self-center">
                  changed
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}


function randomPassword(len = 10) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let p = '';
  for (let i = 0; i < len; i++) p += chars[Math.floor(Math.random() * chars.length)];
  return p + '@1';
}

export default function UsersPage() {
  const { user: me, isSuperAdmin } = useAuth();
  // Only the Super Admin (Owner role) can manage Owner / Admin accounts.
  // The list of role options shown in the create/edit dialog depends on this flag.
  const assignableRoles = isSuperAdmin ? ALL_ROLES : ALL_ROLES.filter((r) => r !== 'Owner' && r !== 'Admin');
  const canManageUser = (target) => isSuperAdmin || !['Owner', 'Admin'].includes(target?.role);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('active'); // 'active' | 'inactive' | 'all'
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwOpen, setPwOpen] = useState(null); // target user for password reset
  const [pwValue, setPwValue] = useState('');
  const [createdInfo, setCreatedInfo] = useState(null); // shows credentials after creation
  const [form, setForm] = useState({
    email: '', name: '', role: 'Designer/Editor', password: '', client_id: '',
    permissions: DEFAULT_PERMS_BY_ROLE['Designer/Editor'],
    team: '', is_head: false, daily_reports_scope: 'own', report_user_ids: [],
    sendInvite: false,
  });
  // Snapshot of the form when the dialog was opened — used to compute the
  // "N changes not saved yet" count on the sticky save bar.
  const [initialForm, setInitialForm] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [roleDefaults, setRoleDefaults] = useState({});   // {role: perms}
  const [teams, setTeams] = useState([]);
  const [userActivity, setUserActivity] = useState({ loading: false, entries: [] });

  const load = async () => {
    const [u, c, rd] = await Promise.all([
      api.get('/users/all'),
      api.get('/clients').catch(() => ({ data: [] })),
      api.get('/access/role-defaults').catch(() => ({ data: {} })),
    ]);
    setUsers(u.data);
    setClients(c.data);
    setRoleDefaults(rd.data?.roles || {});
    setTeams(rd.data?.teams || []);
  };
  useEffect(() => { load(); }, []);

  // Keep the form snapshot in sync so the save bar can diff reliably.
  useEffect(() => {
    if (open && !initialForm) {
      setInitialForm(JSON.parse(JSON.stringify(form)));
    }
    if (!open && initialForm) {
      setInitialForm(null);
      setActiveTab('profile');
      setUserActivity({ loading: false, entries: [] });
    }
    // eslint-disable-next-line
  }, [open]);

  const filtered = useMemo(() => {
    let rows = users;
    if (activeFilter === 'active')   rows = rows.filter((u) => u.is_active !== false);
    if (activeFilter === 'inactive') rows = rows.filter((u) => u.is_active === false);
    if (roleFilter !== 'all') rows = rows.filter((u) => u.role === roleFilter);
    if (q.trim()) {
      const term = q.toLowerCase();
      rows = rows.filter((u) => `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(term));
    }
    return rows;
  }, [users, q, roleFilter, activeFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      email: '', name: '', role: 'Designer/Editor', password: randomPassword(), client_id: '',
      permissions: { ...DEFAULT_PERMS_BY_ROLE['Designer/Editor'] },
      team: '', is_head: false, daily_reports_scope: 'own', report_user_ids: [],
      sendInvite: false,
    });
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email, name: u.name, role: u.role, password: '', client_id: u.client_id || '',
      permissions: { ...(u.permissions || DEFAULT_PERMS_BY_ROLE[u.role] || {}) },
      team: u.team || '',
      is_head: !!u.is_head,
      daily_reports_scope: u.daily_reports_scope || (
        u.role === 'Owner' || u.role === 'Admin' || u.role === 'Operations' ? 'everyone'
        : u.is_head ? 'team' : 'own'
      ),
      report_user_ids: u.report_user_ids || [],
      sendInvite: false,
    });
    setOpen(true);
    // Load this user's activity for the Activity tab
    setUserActivity({ loading: true, entries: [] });
    api.get('/activity/feed', { params: { user_id: u.id, limit: 30 } })
      .then((r) => setUserActivity({ loading: false, entries: r.data?.entries || [] }))
      .catch(() => setUserActivity({ loading: false, entries: [] }));
  };

  const onRoleChange = (role) => {
    setForm((f) => ({
      ...f,
      role,
      client_id: role === 'Client' ? f.client_id : '',
      permissions: { ...(DEFAULT_PERMS_BY_ROLE[role] || {}) },
    }));
  };

  const save = async () => {
    if (!form.email || !form.name) { alert('Email and name are required'); return; }
    if (form.role === 'Client' && !form.client_id) { alert('Pick the client company for a Client user'); return; }
    // Password is required only when creating AND not using the invite flow.
    if (!editing && !form.sendInvite && (!form.password || form.password.length < 6)) {
      alert('Password must be at least 6 chars');
      return;
    }
    try {
      if (editing) {
        const payload = { email: form.email, name: form.name, role: form.role };
        if (form.role === 'Client') payload.client_id = form.client_id;
        if (form.password) payload.password = form.password;
        if (form.role !== 'Client') {
          payload.permissions = form.permissions;
          payload.team = form.team || null;
          payload.is_head = !!form.is_head;
          payload.daily_reports_scope = form.daily_reports_scope;
          payload.report_user_ids = form.daily_reports_scope === 'selected' ? (form.report_user_ids || []) : [];
        }
        await api.patch(`/users/${editing.id}`, payload);
      } else if (form.sendInvite) {
        // Invite flow — backend generates a placeholder password and emails a set-password link.
        const payload = { email: form.email, name: form.name, role: form.role, send_invite: true };
        if (form.role === 'Client') payload.client_id = form.client_id;
        if (form.role !== 'Client') payload.permissions = form.permissions;
        const res = await api.post('/users', payload);
        setCreatedInfo({
          email: res.data.email, name: res.data.name, role: res.data.role,
          invite: res.data.invite || { sent: false },
        });
      } else {
        const payload = { email: form.email, name: form.name, role: form.role, password: form.password };
        if (form.role === 'Client') payload.client_id = form.client_id;
        if (form.role !== 'Client') payload.permissions = form.permissions;
        const res = await api.post('/users', payload);
        setCreatedInfo({ email: res.data.email, password: form.password, name: res.data.name, role: res.data.role });
      }
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save user');
      return;
    }
    setOpen(false);
    setEditing(null);
    load();
  };

  // Send invite email to an existing user (Owner/Admin action from the user row).
  const sendInvite = async (u) => {
    if (!window.confirm(`Send a set-password invite email to ${u.email}?`)) return;
    try {
      const res = await api.post(`/users/${u.id}/invite`);
      if (res.data?.placeholder) {
        alert('Invite created — but no email provider is configured. Check server logs for the link.');
      } else if (res.data?.sent) {
        alert(`Invite email sent to ${u.email}. Link expires in 7 days.`);
      } else {
        alert('Invite created but email delivery failed. See server logs.');
      }
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not send invite');
    }
  };

  const remove = async (u) => {
    if (u.id === me?.id) { alert("You can't delete your own account."); return; }
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete');
      return;
    }
    load();
  };

  // Deactivate (employee exited) / Reactivate. Owner+Admin only. On deactivate
  // the backend releases their pending work (open tasks + collaborator seats);
  // historical data is preserved.
  const toggleActive = async (u) => {
    const willDeactivate = u.is_active !== false;
    if (u.id === me?.id) { toast.error("You can't deactivate your own account."); return; }
    if (willDeactivate) {
      const reason = window.prompt(
        `Deactivate ${u.name}?\n\nThis blocks login, hides them from pickers, and releases their open tasks + collaborator seats. Historical data is kept.\n\nOptional note (visible in activity log):`,
        '',
      );
      if (reason === null) return;
      try {
        const res = await api.post(`/users/${u.id}/toggle-active`, { is_active: false, reason });
        const r = res.data?.released || {};
        toast.success(
          `${u.name} is now inactive. Released ${r.tasks_unassigned || 0} task(s), `
          + `${(r.collab_links_removed || 0) + (r.report_collab_removed || 0) + (r.project_collab_removed || 0)} shared seat(s).`,
        );
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not deactivate');
        return;
      }
    } else {
      if (!window.confirm(`Reactivate ${u.name}? They'll be able to sign in again.`)) return;
      try {
        await api.post(`/users/${u.id}/toggle-active`, { is_active: true });
        toast.success(`${u.name} reactivated.`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not reactivate');
        return;
      }
    }
    load();
  };

  const resetPassword = async () => {
    if (!pwOpen) return;
    if (!pwValue || pwValue.length < 6) { alert('Password must be at least 6 chars'); return; }
    try {
      await api.patch(`/users/${pwOpen.id}`, { password: pwValue });
      setCreatedInfo({ email: pwOpen.email, password: pwValue, name: pwOpen.name, role: pwOpen.role, reset: true });
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to reset password');
      return;
    }
    setPwOpen(null);
    setPwValue('');
  };

  const copyCreds = async () => {
    if (!createdInfo) return;
    const txt = `SHINE Ops Hub login\n\nName: ${createdInfo.name}\nRole: ${createdInfo.role}\nEmail: ${createdInfo.email}\nPassword: ${createdInfo.password}\n\nURL: ${window.location.origin}/login`;
    try { await navigator.clipboard.writeText(txt); alert('Credentials copied to clipboard.'); } catch (_) { /* clipboard denied */ }
  };

  const counts = useMemo(() => {
    const c = { internal: 0, client: 0, total: users.length };
    users.forEach((u) => { if (u.role === 'Client') c.client++; else c.internal++; });
    return c;
  }, [users]);

  return (
    <div>
      <PageHeader
        icon={Users2}
        title="Team & Users"
        subtitle="Add team members, assign roles, and manage client portal logins."
        actions={(
          <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold" data-testid="new-user-btn">
            <Plus className="h-4 w-4 mr-1" /> Add user
          </Button>
        )}
      />

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-xs uppercase tracking-wider text-slate-400">Total users</div>
          <div className="text-2xl font-extrabold text-white mt-1">{counts.total}</div>
        </Card>
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-xs uppercase tracking-wider text-slate-400">Internal team</div>
          <div className="text-2xl font-extrabold text-white mt-1">{counts.internal}</div>
        </Card>
        <Card className="shine-card p-4 rounded-xl">
          <div className="text-xs uppercase tracking-wider text-slate-400">Client logins</div>
          <div className="text-2xl font-extrabold text-white mt-1">{counts.client}</div>
        </Card>
      </div>

      <Card className="shine-card p-3 rounded-xl mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-300/70" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or role…" className="pl-9 bg-[#0f2240] border-amber-500/20 text-white" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-48 h-10 bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a1628] border-amber-500/20 max-h-80">
            <SelectItem value="all">All roles</SelectItem>
            {ALL_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* Active / Inactive / All segmented control */}
        <div className="inline-flex rounded-pill border border-amber-500/20 bg-white/60 overflow-hidden"
             data-testid="active-filter">
          {[
            { v: 'active',   l: 'Active' },
            { v: 'inactive', l: 'Inactive' },
            { v: 'all',      l: 'All' },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => setActiveFilter(o.v)}
              data-testid={`active-filter-${o.v}`}
              className={`px-3.5 h-10 text-sm font-medium transition ${
                activeFilter === o.v ? 'bg-ink text-white' : 'text-ink-2 hover:bg-ink/5'
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">No users match.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((u) => {
            const isInactive = u.is_active === false;
            const clientCompany = u.role === 'Client' && u.client_id ? (clients.find((c) => c.id === u.client_id)?.company || '—') : null;
            return (
              <Card
                key={u.id}
                className={`shine-card p-4 rounded-xl ${isInactive ? 'opacity-70' : ''}`}
                data-testid={`user-card-${u.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isInactive ? 'bg-ink/5 border border-ink/10' : 'bg-amber-500/15 border border-amber-500/30'
                  }`}>
                    <span className={`font-bold ${isInactive ? 'text-ink-3' : 'text-amber-200'}`}>
                      {u.name?.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-bold truncate ${isInactive ? 'text-ink-2 line-through' : 'text-white'}`}>{u.name}</span>
                      {u.id === me?.id && <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[10px]">You</Badge>}
                      {isInactive && (
                        <Badge
                          variant="outline"
                          className="bg-ink/5 border-ink/15 text-ink-2 text-[10px]"
                          data-testid={`badge-inactive-${u.id}`}
                          title={u.deactivated_at ? `Deactivated ${new Date(u.deactivated_at).toLocaleDateString()}` : ''}
                        >
                          Inactive
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 truncate flex items-center gap-1 mt-0.5">
                      <Mail className="h-3 w-3" /> {u.email}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className={ROLE_COLOR[u.role] || defaultColor}>
                        <Shield className="h-3 w-3 mr-1" /> {u.role}
                      </Badge>
                      {clientCompany && (
                        <Badge variant="outline" className="border-sky-500/40 text-sky-300 text-[10px]">{clientCompany}</Badge>
                      )}
                    </div>
                    {isInactive && u.deactivation_reason && (
                      <div className="text-[11px] text-ink-3 italic mt-1.5 line-clamp-2" title={u.deactivation_reason}>
                        “{u.deactivation_reason}”
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-amber-500/10 flex justify-end gap-1 flex-wrap">
                  {canManageUser(u) ? (
                    <>
                      {!isInactive && (
                        <Button size="sm" variant="ghost" onClick={() => sendInvite(u)} className="text-sky-300 hover:bg-sky-500/10" data-testid={`btn-invite-${u.id}`} title="Send set-password email">
                          <Mail className="h-3.5 w-3.5 mr-1" /> Invite
                        </Button>
                      )}
                      {!isInactive && (
                        <Button size="sm" variant="ghost" onClick={() => { setPwOpen(u); setPwValue(randomPassword()); }} className="text-amber-300 hover:bg-amber-500/10" data-testid={`btn-reset-${u.id}`}>
                          <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEdit(u)} className="text-amber-300 hover:bg-amber-500/10" data-testid={`btn-edit-${u.id}`}>
                        <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      {u.id !== me?.id && u.role !== 'Owner' && (
                        isInactive ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleActive(u)}
                            className="text-emerald-300 hover:bg-emerald-500/10"
                            data-testid={`btn-reactivate-${u.id}`}
                            title="Reactivate — the user can sign in again"
                          >
                            <UserCheck className="h-3.5 w-3.5 mr-1" /> Reactivate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleActive(u)}
                            className="text-rose-300 hover:bg-rose-500/10"
                            data-testid={`btn-deactivate-${u.id}`}
                            title="Employee exited — block login and release their pending work"
                          >
                            <UserX className="h-3.5 w-3.5 mr-1" /> Deactivate
                          </Button>
                        )
                      )}
                      {u.id !== me?.id && isInactive && (
                        <Button size="sm" variant="ghost" onClick={() => remove(u)} className="text-red-400 hover:bg-red-500/10" data-testid={`btn-del-${u.id}`} title="Permanently delete">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </>
                  ) : (
                    <span className="text-[11px] text-slate-500 italic flex items-center gap-1">
                      <Shield className="h-3 w-3" /> Super Admin only
                    </span>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-amber-500/10 shrink-0">
            <DialogTitle className="text-white">{editing ? `Edit user · ${editing.name}` : 'Add new user'}</DialogTitle>
            {editing && (
              <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-[#0f2240] p-1 w-fit">
                {['profile', 'access', 'activity'].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setActiveTab(t)}
                    data-state={activeTab === t ? 'active' : 'inactive'}
                    className="shine-pill-dark px-3 py-1 rounded-full text-xs font-medium capitalize text-slate-300"
                    data-testid={`user-tab-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </DialogHeader>
          <div className="space-y-3 overflow-y-auto flex-1 px-6 py-4 min-h-0">
            {(!editing || activeTab === 'profile') && (
            <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="user-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="user-email" />
              </div>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, client_id: v === 'Client' ? form.client_id : '' })}>
                <SelectTrigger className="bg-[#0f2240] border-amber-500/20" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a1628] border-amber-500/20 max-h-72">
                  {assignableRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              {!isSuperAdmin && (
                <p className="text-[11px] text-slate-500 mt-1">Only the Super Admin can grant Owner / Admin roles.</p>
              )}
            </div>
            {form.role === 'Client' && (
              <div>
                <Label>Client company</Label>
                <Select value={form.client_id || '_none'} onValueChange={(v) => setForm({ ...form, client_id: v === '_none' ? '' : v })}>
                  <SelectTrigger className="bg-[#0f2240] border-amber-500/20" data-testid="user-client"><SelectValue placeholder="Pick the client company" /></SelectTrigger>
                  <SelectContent className="bg-[#0a1628] border-amber-500/20">
                    <SelectItem value="_none">— Choose —</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-slate-400 mt-1">
                  Client users only see projects and reports for the company you pick here.
                </div>
              </div>
            )}
            <div>
              {!editing && (
                <label
                  className="flex items-start gap-2 bg-[#0f2240] border border-amber-500/20 rounded-md p-2 mb-3 cursor-pointer hover:border-amber-500/40"
                  data-testid="send-invite-row"
                >
                  <Checkbox
                    id="send-invite"
                    checked={!!form.sendInvite}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, sendInvite: !!v }))}
                    className="border-amber-500/40 data-[state=checked]:bg-amber-500 data-[state=checked]:text-[#0a1628] mt-0.5"
                    data-testid="send-invite-checkbox"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white">Send invite email instead of setting a password now</div>
                    <div className="text-[11px] text-slate-400 leading-snug">
                      Emails the user a link to choose their own password. Link expires in 7 days.
                    </div>
                  </div>
                </label>
              )}
              {(!form.sendInvite || editing) && (
                <>
                  <Label>{editing ? 'New password (leave blank to keep current)' : 'Initial password'}</Label>
                  <div className="flex gap-2">
                    <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="user-password" />
                    <Button type="button" variant="outline" onClick={() => setForm({ ...form, password: randomPassword() })} className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10">Generate</Button>
                  </div>
                  {!editing && <div className="text-[11px] text-slate-400 mt-1">Share this password with the new user privately — they can change it later.</div>}
                </>
              )}
            </div>

            </>)}

            {editing && activeTab === 'access' && form.role !== 'Client' && (
              <AccessTabContent
                form={form}
                setForm={setForm}
                users={users}
                editingId={editing.id}
                roleDefaults={roleDefaults}
                teams={teams}
                isSuperAdmin={isSuperAdmin}
              />
            )}
            {editing && activeTab === 'access' && form.role === 'Client' && (
              <div className="rounded-md border border-amber-500/20 bg-[#0f2240]/40 p-5 text-center text-sm text-slate-400">
                Client users don't have module access — they only see their own company's work.
              </div>
            )}

            {editing && activeTab === 'activity' && (
              <div className="space-y-2" data-testid="user-activity-tab">
                <div className="text-xs text-slate-400">
                  What <b className="text-slate-200">{editing.name}</b> has been doing. From the Activity feed, newest first.
                </div>
                {userActivity.loading ? (
                  <div className="py-6 text-center text-sm text-slate-500">Loading…</div>
                ) : userActivity.entries.length === 0 ? (
                  <div className="py-6 text-center text-sm text-slate-500 italic">No activity yet.</div>
                ) : (
                  <div className="space-y-1.5">
                    {userActivity.entries.map((r) => (
                      <div key={r.id} className="flex items-start gap-2 bg-[#0f2240]/60 border border-amber-500/15 rounded-md px-3 py-2 text-sm">
                        <div className="min-w-0 flex-1">
                          <div className="text-slate-200">{r.description || r.action}</div>
                          <div className="text-[11px] text-slate-500">{r.entity_type}</div>
                        </div>
                        <div className="text-[11px] text-slate-500 flex-shrink-0">
                          {(r.created_at || '').slice(0, 16).replace('T', ' ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-amber-500/10 shrink-0 bg-[#0a1628] flex-row items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
            {editing ? (
              <div className="text-[11px] text-slate-400 flex-1 min-w-0">
                {(() => {
                  const n = diffCount(form, initialForm);
                  return n > 0
                    ? <span className="text-amber-200 font-semibold">{n} change{n === 1 ? '' : 's'} not saved yet</span>
                    : <span>Changes are recorded in Activity.</span>;
                })()}
              </div>
            ) : <div className="flex-1" />}
            <div className="flex items-center gap-2">
              {editing && (
                <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-300 hover:bg-amber-500/10" data-testid="user-cancel">
                  Cancel
                </Button>
              )}
              <Button onClick={save} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]" data-testid="user-save">
                {editing ? 'Save changes' : 'Create user'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwOpen} onOpenChange={(o) => !o && (setPwOpen(null), setPwValue(''))}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
          {pwOpen && (
            <>
              <DialogHeader><DialogTitle className="text-white">Reset password — {pwOpen.name}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Label>New password</Label>
                <div className="flex gap-2">
                  <Input value={pwValue} onChange={(e) => setPwValue(e.target.value)} className="bg-[#0f2240] border-amber-500/20" data-testid="reset-pw-input" />
                  <Button type="button" variant="outline" onClick={() => setPwValue(randomPassword())} className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10">Generate</Button>
                </div>
                <div className="text-[11px] text-slate-400">User will need to log in again with this new password. Share it privately.</div>
              </div>
              <DialogFooter>
                <Button onClick={resetPassword} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]" data-testid="reset-pw-save">Reset password</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Credentials confirmation */}
      <Dialog open={!!createdInfo} onOpenChange={(o) => !o && setCreatedInfo(null)}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
          {createdInfo && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">
                  {createdInfo.reset ? 'Password reset ✓'
                    : createdInfo.invite ? 'Invite sent ✓'
                    : 'User created ✓'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {createdInfo.invite ? (
                  <>
                    <p className="text-sm text-slate-300">
                      {createdInfo.invite.sent
                        ? <>An invite email has been sent to <span className="text-amber-200 font-mono">{createdInfo.email}</span>. They can click the link inside to set their password. The link expires in 7 days.</>
                        : <>The user was created but the invite email couldn&apos;t be delivered right now. Use the <span className="text-white">Invite</span> button on the user row to try again, or share the login URL manually.</>
                      }
                    </p>
                    <div className="bg-[#0f2240] border border-amber-500/30 rounded-lg p-3 space-y-1 text-sm">
                      <div><span className="text-slate-400">Name:</span> <span className="text-white font-semibold">{createdInfo.name}</span></div>
                      <div><span className="text-slate-400">Role:</span> <span className="text-white">{createdInfo.role}</span></div>
                      <div><span className="text-slate-400">Email:</span> <span className="text-amber-200 font-mono">{createdInfo.email}</span></div>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-300">
                      Share these credentials with the user. They can log in immediately at the Ops Hub URL.
                    </p>
                    <div className="bg-[#0f2240] border border-amber-500/30 rounded-lg p-3 space-y-1 text-sm">
                      <div><span className="text-slate-400">Name:</span> <span className="text-white font-semibold">{createdInfo.name}</span></div>
                      <div><span className="text-slate-400">Role:</span> <span className="text-white">{createdInfo.role}</span></div>
                      <div><span className="text-slate-400">Email:</span> <span className="text-amber-200 font-mono">{createdInfo.email}</span></div>
                      <div><span className="text-slate-400">Password:</span> <span className="text-amber-200 font-mono">{createdInfo.password}</span></div>
                    </div>
                    <Button onClick={copyCreds} variant="outline" className="w-full border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
                      <Copy className="h-4 w-4 mr-1" /> Copy as message
                    </Button>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button onClick={() => setCreatedInfo(null)} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]">Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
